import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { App } from '@df/core/app';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import findIndex from 'lodash-es/findIndex';
import {
    addKlevuContextRecommendationsVariant,
    addKlevuRecommendationsVariant
} from '../../../../module/klevu-search/klevu-recommendations.config';
import { ProductComponent } from '../../../product/product/product.component';
import { getFields } from '../model-carousel/model-carousel.config';
import { IModelCarouselData } from '../model-carousel/model-carousel.interface';
import { AbstractProductsCarouselComponent, IAbstractProductCarouselData } from './abstract-products-carousel.component';
export interface IPageComponentProductCarouselData extends IAbstractProductCarouselData {
    carouselVariant: string;
}

let uid = 0;

export const fields = addKlevuContextRecommendationsVariant(addKlevuRecommendationsVariant(getFields('product')));
const useProductsIdsFromPdp = (data: KlevuSearch.IRecommendationsModelCarouselLoadRequest) => {
    if (typeof window !== 'undefined' && window?.app?.meta?.model?.type === 'product') {
        return data?.klevuTypeId === KlevuSearch.ERecommendationTypeId.SimilarProducts;
    }
    return false;
};
const klevuProductsFieldIndex = findIndex(fields, i => i.key === 'klevuProductsIds');
if (klevuProductsFieldIndex > -1) {
    const field = fields[klevuProductsFieldIndex];
    if (field) {
        const coreCanShow = field.canShow;
        field.canShow = (
            data: KlevuSearch.IRecommendationsModelCarouselLoadRequest,
            outletOptions: NICmsContentComponent.IOutletOptions
        ) => {
            if (coreCanShow && !coreCanShow(data, outletOptions)) {
                return false;
            }
            if (useProductsIdsFromPdp(data)) {
                return false;
            }
            return true;
        };
    }
}
const variantField = fields.find(i => i.key === 'variant') as NICmsContentField.IChoiceField;
if (Array.isArray(variantField.options)) {
    variantField.options.push({ key: 'attribute', title: 'Range' });
}
@Component({
    selector: 'products-carousel',
    templateUrl: 'products-carousel.html',
    styleUrls: ['products-carousel.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        SizeClassDirective,
        CommonModule,
        ProductComponent,
        GridModule,
        ICmsClientModule,
        TranslationModule,
        CarouselModule,
        ProductComponent
    ]
})
@ICmsComponent<PageComponentProductsCarouselComponent>({
    id: 'product_carousel_layout',
    name: 'Product Carousel Layout',
    tags: ['Product', 'Carousel'],
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'carouselVariant',
            type: NICmsContentField.EFieldType.choice,
            title: 'Carousel Variant',
            required: true,
            default: 'full_details',
            options: [
                { key: 'full_details', title: 'Full details carousel' },
                { key: 'reduced_details', title: 'Reduced details carousel' },
                { key: 'smaller_squares', title: 'Smaller squares' },
                { key: 'smaller_squares_title', title: 'Smaller squares with title' },
                { key: 'new_in', title: 'New in' }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'showCarouselOnFacets',
            type: NICmsContentField.EFieldType.choice,
            title: 'Show carousel on facets',
            default: 'show',
            options: [
                { key: 'show', title: 'Show' },
                { key: 'hide', title: 'Hide' }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'attribute',
            type: NICmsContentField.EFieldType.choice,
            title: 'Additional attribute',
            options: () => {
                if (typeof window !== 'undefined') {
                    return [{ key: undefined, value: undefined, title: 'undefined' }, ...getAttributeOptions()];
                }
                return [];
            },
            canShow: (data: IModelCarouselData) => (data?.variant as string) === 'attribute'
        },
        ...fields
    ]
})
export class PageComponentProductsCarouselComponent
    extends AbstractProductsCarouselComponent<IPageComponentProductCarouselData >
{
    @ICmsContent({
        type: 'component'
    })
    override component?: IPageComponentProductCarouselData;

    uid = `products-carousel-${uid++}`;

}

export function getAttributeOptions() {
    return (
        (<App>window['app'])?.resources?.attribute?.getAllExisting().map(i => {
            return {
                key: i.id,
                value: i.id,
                title: i.data?.label
            };
        }) || []
    );
}
